import { bundlePricingLineTotal } from "@/pages/deals/components/commercials/DealCommercialsV2/ContractTermsV2/bundlePricing";
import { totalAfterDiscount } from "@/pages/deals/components/commercials/DealCommercialsV2/ContractTermsV2/consts";
import {
  BillingFrequency,
  ChargeType,
  Pricing,
  PricingModel,
  ServiceDuration,
  ServiceItem
} from "./types";

/**
 * Whether a service on this term can go in a bundle. Flexible pricing, a custom
 * duration and a custom billing frequency are all set per deal, so a bundle has
 * no default to carry for them (the backend refuses them too).
 */
export const isBundleableTerm = (
  pricingModel: number,
  term: Pricing
): boolean =>
  pricingModel !== PricingModel.FLEXIBLE &&
  term.serviceItemPeriods !== ServiceDuration.CUSTOM &&
  term.billingFrequency !== BillingFrequency.CUSTOM;

// The backend's cap on a bundled service's quantity.
const MAX_BUNDLE_ITEM_QUANTITY = 1_000_000;

/** The typed quantity as a number, or null when the backend would refuse it. */
export const parseBundleQuantity = (value: string): number | null => {
  if (value.trim() === "") return null;
  const quantity = Number(value);
  return Number.isFinite(quantity) &&
    quantity > 0 &&
    quantity <= MAX_BUNDLE_ITEM_QUANTITY
    ? quantity
    : null;
};

/** The service's terms a bundle may use, in the service's own order. */
export const bundleableTerms = (service: ServiceItem): Pricing[] =>
  service.pricing.filter((term) =>
    isBundleableTerm(service.pricingModel, term)
  );

/**
 * One service's share of a bundle total on a term at the catalog price, by the
 * bundle price rule the backend and the deal drawer share.
 */
export const bundleLineTotal = (
  pricingModel: number,
  chargeType: ChargeType | null,
  term: Pricing,
  quantity: number
): number =>
  bundlePricingLineTotal({
    pricingModel,
    chargeType,
    price: term.price,
    quantity,
    period: term.serviceItemPeriods
  });

// Float noise a summed total can carry; the backend allows the same.
const TOTAL_NOISE = 1e-6;

export type BundleDiscountError = "percentage" | "negative" | "aboveTotal";

/**
 * The typed discount as a number, or why the backend would refuse it. An empty
 * field is no discount.
 */
export const parseBundleDiscount = (
  value: string,
  isPercentage: boolean,
  total: number
): { amount: number | null; error: BundleDiscountError | null } => {
  if (value.trim() === "") return { amount: null, error: null };
  const amount = Number(value);
  if (!Number.isFinite(amount)) {
    return { amount: null, error: isPercentage ? "percentage" : "negative" };
  }
  if (isPercentage) {
    return amount < 0 || amount > 100
      ? { amount: null, error: "percentage" }
      : { amount, error: null };
  }
  if (amount < 0) return { amount: null, error: "negative" };
  if (amount - total > TOTAL_NOISE)
    return { amount: null, error: "aboveTotal" };
  return { amount, error: null };
};

/** The bundle total less its discount, never below zero, as the backend computes it. */
export const bundleDiscountedTotal = (
  total: number,
  discountAmount: number | null,
  isDiscountPercentage: boolean | null
): number =>
  Math.max(0, totalAfterDiscount(total, discountAmount, isDiscountPercentage));
